import { api } from "./api-client";

export interface User {
    id: number;
    email: string;
    name: string;
}

export function setToken(token: string): void {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `token=${token}; path=/; max-age=86400; SameSite=Lax${secure}`;
}

export async function login(email: string): Promise<{ message: string }> {
    return api.public.post("/auth/login", { email });
}

export async function register(email: string, name: string): Promise<{ message: string }> {
    return api.public.post("/auth/register", { email, name });
}

export async function verifyOTP(email: string, otpCode: string): Promise<{ token: string }> {
    return api.public.post("/auth/verify", { email, otp_code: otpCode });
}

export async function verifyMagicLink(token: string): Promise<{ token: string }> {
    return api.public.post("/auth/verify", { token });
}

export async function passkeyLogin(): Promise<{ token: string }> {
    const { session_id, options } = await api.public.post<{ session_id: string; options: { publicKey: PublicKeyCredentialRequestOptionsJSON } }>("/auth/passkey/login/begin", {});

    const credential = await navigator.credentials.get({
        publicKey: {
            ...options.publicKey,
            challenge: base64URLToBuffer(options.publicKey.challenge),
            allowCredentials: options.publicKey.allowCredentials?.map((c) => ({
                id: base64URLToBuffer(c.id),
                type: "public-key" as const,
                transports: c.transports as AuthenticatorTransport[] | undefined,
            })),
        },
    }) as PublicKeyCredential;

    if (!credential) {
        throw new Error("Passkey login cancelled");
    }

    const response = credential.response as AuthenticatorAssertionResponse;

    return api.public.post(`/auth/passkey/login/finish?session_id=${session_id}`, {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
            authenticatorData: bufferToBase64URL(response.authenticatorData),
            clientDataJSON: bufferToBase64URL(response.clientDataJSON),
            signature: bufferToBase64URL(response.signature),
            userHandle: response.userHandle ? bufferToBase64URL(response.userHandle) : null,
        },
    });
}

export function isPasskeySupported(): boolean {
    return typeof window !== "undefined" &&
        typeof window.PublicKeyCredential !== "undefined" &&
        typeof navigator.credentials !== "undefined";
}

function base64URLToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
    const binary = atob(base64 + pad);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function bufferToBase64URL(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// WebAuthn type for passkey options
interface PublicKeyCredentialRequestOptionsJSON {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: { id: string; type: string; transports?: string[] }[];
    userVerification?: UserVerificationRequirement;
}
